import dayjs from 'dayjs';
import { Select } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import { LocalAtmOutlined } from '@mui/icons-material';
import EditableLable from '../../../common/labels/EditableLabel/EditableLable';
import TableBasicLabel from '../../../common/labels/TableBasicLabel/TableBasicLabel';
import { getNumberWithDelimeter, isValidByRateType } from '../../../../utils/helpers';
import { getHoursByDate } from '../helpers';
import AddGradientIcon from '../../../common/Icons/AddGradientIcon';
import TimesheetComment from './TimesheetComment/TimesheetComment';
import SearchLocation from '../../../common/SearchLocation/SearchLocation';
import StyledLocationContainer from './EditTimesheetModal/components/StyledLocationContainer';
import ThreeDotMenu from '../../../common/ThreeDotMenu/ThreeDotMenu';
import { timesheetsStatuses } from '../../../../utils/constants';
import colors from '../../../../scss/colors';
import { rateEnum, submissionEnum } from '../../../../utils/enums';
import { CustomTooltip } from '../../../common/Tooltip/Tooltip';
import OutlinedCheckBox from '../../../common/Form/OutlinedCheckBox/OutlinedCheckBox';

const TYPES = {
  CHECKED: 'checked',
  LOCATION: 'Location',
  WORK_TYPE: 'WorkType',
  CODE: 'Code',
  COMMENT: 'Comment',
  HOURS_BY_DATE: 'TimesheetHoursByDates'
};

const ThreeDotContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  gap: '24px'
}));

const ThreeDotItem = styled('p')(() => ({
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '20px',
  lineHeight: '24px',
  color: colors.MEDIUM_GREY,
  cursor: 'pointer'
}));

const styleConfig = {
  location: { width: { min: '100px', max: '1.6fr' } },
  worktype: { width: { min: '110px', max: '1.4fr' } },
  comment: { width: { min: '120px', max: '1.5fr' } },
  date: { width: { min: '44px', max: '64px' } },
  threedot: { width: { min: '36px', max: '36px' } }
};

const styleConfigMonthly = {
  checkboxes: { width: { min: '48px', max: '48px' } },
  location: { width: { min: '110px', max: '1.6fr' } },
  worktype: { width: { min: '120px', max: '1.4fr' } },
  code: { width: { min: '100px', max: '1.2fr' } },
  comment: { width: { min: '120px', max: '1.4fr' } },
  date: { width: { min: '60px', max: '64px' } },
  threedot: { width: { min: '36px', max: '36px' } },
  total: { width: { min: '50px', max: '64px' } }
};

const basicFontSize = '14px';

const forcedBrakeWidth = '25px';

const selectStyles = {
  width: '100%',
  height: '100%',
  paddingLeft: '20px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${colors.PAGE_BACKGROUND}`,
    borderRadius: '10px'
  },
  '& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input': {
    padding: '0 32px 0 0'
  },
  '& .MuiSvgIcon-root': {
    color: colors.LIGHT_GREY,
    fontSize: '18px',
    right: '22px'
  },
  color: colors.NAVY_BLUE,
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '17px',
  fieldset: {
    legend: {
      width: 'unset'
    }
  }
};

const StyledLabel = styled('div')(() => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  fontWeight: 400,
  fontSize: '14px',
  fontStyle: 'normal',
  color: colors.NAVY_BLUE
}));

const FooterCell = styled('div')(({ radius = '0', extrastyles = {} }) => ({
  width: '100%',
  height: '100%',
  paddingTop: '8px',
  paddingBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: radius,
  ...extrastyles
}));

const fixJSfailWithMath = (num) => {
  if (num % 1 === 0) return num;
  return num?.toFixed(2);
};

/**
 * Builds the extraFooter per-diem checkbox for a specific date in the weekly columns.
 * Mirrors the logic in monthly2columns extraFooter.
 */
const buildWeeklyPerDiemExtraFooter = ({
  currentDate,
  perdiems,
  allSectionsPerDiems,
  sectionId,
  fullDisabled,
  changePerDiem,
  setOpenPerDiemError,
  t
}) => {
  const perDiemItem = perdiems?.find((item) => dayjs(item.Date).isSame(currentDate, 'day'));

  if (!perDiemItem) return null;

  const conflictInOtherSection = allSectionsPerDiems
    ?.filter((sp) => sp.sectionId !== sectionId)
    .some((sp) => sp.perdiems?.some((pd) => dayjs(pd.Date).isSame(currentDate, 'day') && pd.IsPerDiem));

  const isDisabled = fullDisabled || (conflictInOtherSection && !perDiemItem.IsPerDiem);

  return (
    <OutlinedCheckBox
      key={perDiemItem.Date}
      value={perDiemItem.IsPerDiem}
      onChange={() => {
        if (conflictInOtherSection && !perDiemItem.IsPerDiem) {
          setOpenPerDiemError(t('dashboard.timesheet.validations.perdiemDuplicate'));
          return;
        }
        changePerDiem(perDiemItem.Date, !perDiemItem.IsPerDiem, sectionId);
      }}
      disabled={isDisabled}
    />
  );
};

const columns = (
  sectionId,
  startDateOfPeriod,
  changeHandler,
  t,
  isAllItemsChecked,
  mainCheckboxHandler,
  addHandler,
  allowedWorkTypes,
  isEdit,
  deleteLine,
  rateType,
  currentMonth,
  submissionType,
  setOpenRateError,
  status,
  contractor,
  perdiems,
  changePerDiem,
  fullDisabled,
  allSectionsPerDiems,
  setOpenPerDiemError
) => {
  return [
    {
      id: '2',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.timesheet.table.location')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 15px"
        />
      ),
      width: styleConfig.location.width,
      value: (data) => (
        <div
          style={{
            width: '100%',
            height: '100%',
            padding: '6px 10px 6px 0px'
          }}
        >
          <StyledLocationContainer
            color={colors.NAVY_BLUE}
            height="100%"
            bordercolor={isEdit ? colors.PAGE_BACKGROUND : 'transparent'}
          >
            <SearchLocation
              disabled={!isEdit}
              value={data.Location}
              changeHandler={(value) => changeHandler(TYPES.LOCATION, data.Id, value)}
              withClear={false}
              placeholder={`${t('dashboard.timesheet.table.location')} *`}
            />
          </StyledLocationContainer>
        </div>
      ),
      footer: () =>
        isEdit &&
        ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(status) && (
          <div
            role="presentation"
            onClick={addHandler}
            style={{
              cursor: 'pointer',
              display: 'flex',
              flexWrap: 'nowrap',
              flexDirection: 'row',
              marginRight: 'auto',
              gap: '4px',
              color: colors.ICON_GREY
            }}
          >
            <CustomTooltip
              title={t('common.general.addrow')}
              placement="bottom-start"
              theme={{
                backgroundColor: colors.LIGHT_BLUE,
                color: colors.WHITE_TXT,
                padding: '6px 8px',
                fontSize: 13,
                borderRadius: '8px'
              }}
            >
              <button style={{ border: 'none', background: 'transparent' }}>
                <AddGradientIcon />
              </button>
            </CustomTooltip>
          </div>
        ),
      extraFooter: () => (
        <div className="text-sm text-navy-blue flex h-full">
          <div className="flex items-center gap-2">
            <LocalAtmOutlined />
            <span>Per Diem</span>
          </div>
        </div>
      )
    },
    {
      id: '3',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.timesheet.table.worktype')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 8px"
        />
      ),
      width: styleConfig.worktype.width,
      value: (data) => (
        <div
          style={{
            width: '100%',
            height: '100%',
            padding: '6px 10px'
          }}
        >
          {isEdit ? (
            <Select
              value={data?.WorkType?.Id}
              onChange={(e) => {
                changeHandler(
                  TYPES.WORK_TYPE,
                  data.Id,
                  allowedWorkTypes.find((allowedType) => allowedType.Id === e.target.value)
                );
                data.WorkType.Id = e.target.value;
              }}
              sx={selectStyles}
              IconComponent={ExpandMoreIcon}
              displayEmpty
              renderValue={data.WorkType !== '' ? undefined : () => `${t('dashboard.timesheet.table.worktype')}*`}
            >
              {allowedWorkTypes.map((type) => (
                <MenuItem key={type.Id} value={type.Id}>
                  {type.Name}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <StyledLabel>{data?.WorkType?.Name}</StyledLabel>
          )}
        </div>
      ),
      footer: () => '',
      extraFooter: () => ''
    },
    {
      id: 'code',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.timesheet.table.code')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 20px"
        />
      ),
      width: styleConfig.comment.width,
      value: (data) => {
        return (
          <TimesheetComment
            disabled={!isEdit}
            title={t('dashboard.timesheet.table.code')}
            comment={data.CostCode}
            author={`${contractor.FirstName} ${contractor.LastName}`}
            changeHandler={(newValue) => changeHandler(TYPES.CODE, data.Id, newValue)}
          />
        );
      },
      footer: () => '',
      extraFooter: () => ''
    },
    {
      id: 'comment',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.timesheet.table.comment')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 20px"
        />
      ),
      width: styleConfig.comment.width,
      value: (data) => {
        return (
          <TimesheetComment
            disabled={!isEdit}
            comment={data.Comment}
            author={`${contractor.FirstName} ${contractor.LastName}`}
            changeHandler={(newValue) => changeHandler(TYPES.COMMENT, data.Id, newValue)}
          />
        );
      },
      footer: () => '',
      extraFooter: () => ''
    },
    {
      id: '4',
      width: styleConfig.date.width,
      name: () => (
        <div className="d-flex flex-column">
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(startDateOfPeriod).format('ddd')}
            fontColor="white"
            fontSize="14px"
            fontWeight="400"
            lineHeight="17px"
          />
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(startDateOfPeriod).format('DD')}
            fontColor="white"
            notNumber
          />
        </div>
      ),
      value: (data) => (
        <EditableLable
          disabled={
            !isEdit ||
            (dayjs(startDateOfPeriod).get('month') !== currentMonth && submissionType === submissionEnum.MONTHLY)
          }
          key="key1"
          value={getNumberWithDelimeter(getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod)), isEdit)}
          changeHandler={(newValue) => {
            if (isValidByRateType(newValue, rateType)) {
              changeHandler(TYPES.HOURS_BY_DATE, data.Id, newValue, startDateOfPeriod);
            } else {
              setOpenRateError(
                rateType === rateEnum.HOURLY
                  ? t('dashboard.timesheet.validations.max24h')
                  : t('dashboard.timesheet.validations.max1day')
              );
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod)),
                startDateOfPeriod
              );
            }
          }}
        />
      ),
      footer: (data) => (
        <FooterCell radius="10px 0 0 10px">
          <TableBasicLabel
            text={data.reduce(
              (count, row) => count + Number(getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod))),
              0
            )}
            fontSize={basicFontSize}
            isEdit={isEdit}
          />
        </FooterCell>
      ),
      extraFooter: () =>
        buildWeeklyPerDiemExtraFooter({
          currentDate: dayjs(startDateOfPeriod),
          perdiems,
          allSectionsPerDiems,
          sectionId,
          fullDisabled,
          changePerDiem,
          setOpenPerDiemError,
          t
        })
    },
    {
      id: '5',
      width: styleConfig.date.width,
      name: () => (
        <div className="d-flex flex-column">
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(startDateOfPeriod).add(1, 'day')).format('ddd')}
            fontColor="white"
            fontSize="14px"
            fontWeight="400"
            lineHeight="17px"
          />
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(startDateOfPeriod).add(1, 'day')).format('DD')}
            fontColor="white"
            notNumber
          />
        </div>
      ),
      value: (data) => (
        <EditableLable
          disabled={
            !isEdit ||
            (dayjs(startDateOfPeriod).add(1, 'day').get('month') !== currentMonth &&
              submissionType === submissionEnum.MONTHLY)
          }
          key="key2"
          value={getNumberWithDelimeter(
            getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(1, 'day')),
            isEdit
          )}
          changeHandler={(newValue) => {
            if (isValidByRateType(newValue, rateType)) {
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                newValue,
                dayjs(dayjs(startDateOfPeriod).add(1, 'day')).toString()
              );
            } else {
              setOpenRateError(
                rateType === rateEnum.HOURLY
                  ? t('dashboard.timesheet.validations.max24h')
                  : t('dashboard.timesheet.validations.max1day')
              );
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(1, 'day')),
                dayjs(dayjs(startDateOfPeriod).add(1, 'day')).toString()
              );
            }
          }}
        />
      ),
      footer: (data) => (
        <FooterCell>
          <TableBasicLabel
            text={data.reduce(
              (count, row) =>
                count + Number(getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(1, 'day'))),
              0
            )}
            fontSize={basicFontSize}
            isEdit={isEdit}
          />
        </FooterCell>
      ),
      extraFooter: () =>
        buildWeeklyPerDiemExtraFooter({
          currentDate: dayjs(startDateOfPeriod).add(1, 'day'),
          perdiems,
          allSectionsPerDiems,
          sectionId,
          fullDisabled,
          changePerDiem,
          setOpenPerDiemError,
          t
        })
    },
    {
      id: '6',
      width: styleConfig.date.width,
      name: () => (
        <div className="d-flex flex-column">
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(startDateOfPeriod).add(2, 'day')).format('ddd')}
            fontColor="white"
            fontSize="14px"
            fontWeight="400"
            lineHeight="17px"
          />
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(startDateOfPeriod).add(2, 'day')).format('DD')}
            fontColor="white"
            notNumber
          />
        </div>
      ),
      value: (data) => (
        <EditableLable
          disabled={
            !isEdit ||
            (dayjs(startDateOfPeriod).add(2, 'day').get('month') !== currentMonth &&
              submissionType === submissionEnum.MONTHLY)
          }
          key="key3"
          value={getNumberWithDelimeter(
            getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(2, 'day')),
            isEdit
          )}
          changeHandler={(newValue) => {
            if (isValidByRateType(newValue, rateType)) {
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                newValue,
                dayjs(dayjs(startDateOfPeriod).add(2, 'day')).toString()
              );
            } else {
              setOpenRateError(
                rateType === rateEnum.HOURLY
                  ? t('dashboard.timesheet.validations.max24h')
                  : t('dashboard.timesheet.validations.max1day')
              );
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(2, 'day')),
                dayjs(dayjs(startDateOfPeriod).add(2, 'day')).toString()
              );
            }
          }}
        />
      ),
      footer: (data) => (
        <FooterCell>
          <TableBasicLabel
            text={data.reduce(
              (count, row) =>
                count + Number(getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(2, 'day'))),
              0
            )}
            fontSize={basicFontSize}
            isEdit={isEdit}
          />
        </FooterCell>
      ),
      extraFooter: () =>
        buildWeeklyPerDiemExtraFooter({
          currentDate: dayjs(startDateOfPeriod).add(2, 'day'),
          perdiems,
          allSectionsPerDiems,
          sectionId,
          fullDisabled,
          changePerDiem,
          setOpenPerDiemError,
          t
        })
    },
    {
      id: '7',
      width: styleConfig.date.width,
      name: () => (
        <div className="d-flex flex-column">
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(startDateOfPeriod).add(3, 'day')).format('ddd')}
            fontColor="white"
            fontSize="14px"
            fontWeight="400"
            lineHeight="17px"
          />
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(startDateOfPeriod).add(3, 'day')).format('DD')}
            fontColor="white"
            notNumber
          />
        </div>
      ),
      value: (data) => (
        <EditableLable
          disabled={
            !isEdit ||
            (dayjs(startDateOfPeriod).add(3, 'day').get('month') !== currentMonth &&
              submissionType === submissionEnum.MONTHLY)
          }
          key="key4"
          value={getNumberWithDelimeter(
            getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(3, 'day')),
            isEdit
          )}
          changeHandler={(newValue) => {
            if (isValidByRateType(newValue, rateType)) {
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                newValue,
                dayjs(dayjs(startDateOfPeriod).add(3, 'day')).toString()
              );
            } else {
              setOpenRateError(
                rateType === rateEnum.HOURLY
                  ? t('dashboard.timesheet.validations.max24h')
                  : t('dashboard.timesheet.validations.max1day')
              );
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(3, 'day')),
                dayjs(dayjs(startDateOfPeriod).add(3, 'day')).toString()
              );
            }
          }}
        />
      ),
      footer: (data) => (
        <FooterCell>
          <TableBasicLabel
            text={data.reduce(
              (count, row) =>
                count + Number(getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(3, 'day'))),
              0
            )}
            fontSize={basicFontSize}
            isEdit={isEdit}
          />
        </FooterCell>
      ),
      extraFooter: () =>
        buildWeeklyPerDiemExtraFooter({
          currentDate: dayjs(startDateOfPeriod).add(3, 'day'),
          perdiems,
          allSectionsPerDiems,
          sectionId,
          fullDisabled,
          changePerDiem,
          setOpenPerDiemError,
          t
        })
    },
    {
      id: '8',
      width: styleConfig.date.width,
      name: () => (
        <div className="d-flex flex-column">
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(startDateOfPeriod).add(4, 'day')).format('ddd')}
            fontColor="white"
            fontSize="14px"
            fontWeight="400"
            lineHeight="17px"
          />
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(startDateOfPeriod).add(4, 'day')).format('DD')}
            fontColor="white"
            notNumber
          />
        </div>
      ),
      value: (data) => (
        <EditableLable
          disabled={
            !isEdit ||
            (dayjs(startDateOfPeriod).add(4, 'day').get('month') !== currentMonth &&
              submissionType === submissionEnum.MONTHLY)
          }
          key="key5"
          value={getNumberWithDelimeter(
            getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(4, 'day')),
            isEdit
          )}
          changeHandler={(newValue) => {
            if (isValidByRateType(newValue, rateType)) {
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                newValue,
                dayjs(dayjs(startDateOfPeriod).add(4, 'day')).toString()
              );
            } else {
              setOpenRateError(
                rateType === rateEnum.HOURLY
                  ? t('dashboard.timesheet.validations.max24h')
                  : t('dashboard.timesheet.validations.max1day')
              );
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(4, 'day')),
                dayjs(dayjs(startDateOfPeriod).add(4, 'day')).toString()
              );
            }
          }}
        />
      ),
      footer: (data) => (
        <FooterCell>
          <TableBasicLabel
            text={data.reduce(
              (count, row) =>
                count + Number(getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(4, 'day'))),
              0
            )}
            fontSize={basicFontSize}
            isEdit={isEdit}
          />
        </FooterCell>
      ),
      extraFooter: () =>
        buildWeeklyPerDiemExtraFooter({
          currentDate: dayjs(startDateOfPeriod).add(4, 'day'),
          perdiems,
          allSectionsPerDiems,
          sectionId,
          fullDisabled,
          changePerDiem,
          setOpenPerDiemError,
          t
        })
    },
    {
      id: '9',
      width: styleConfig.date.width,
      name: () => (
        <div className="d-flex flex-column">
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(startDateOfPeriod).add(5, 'day')).format('ddd')}
            fontColor="white"
            fontSize="14px"
            fontWeight="400"
            lineHeight="17px"
          />
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(startDateOfPeriod).add(5, 'day')).format('DD')}
            fontColor="white"
            notNumber
          />
        </div>
      ),
      value: (data) => (
        <EditableLable
          disabled={
            !isEdit ||
            (dayjs(startDateOfPeriod).add(5, 'day').get('month') !== currentMonth &&
              submissionType === submissionEnum.MONTHLY)
          }
          key="key6"
          value={getNumberWithDelimeter(
            getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(5, 'day')),
            isEdit
          )}
          changeHandler={(newValue) => {
            if (isValidByRateType(newValue, rateType)) {
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                newValue,
                dayjs(dayjs(startDateOfPeriod).add(5, 'day')).toString()
              );
            } else {
              setOpenRateError(
                rateType === rateEnum.HOURLY
                  ? t('dashboard.timesheet.validations.max24h')
                  : t('dashboard.timesheet.validations.max1day')
              );
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(5, 'day')),
                dayjs(dayjs(startDateOfPeriod).add(5, 'day')).toString()
              );
            }
          }}
        />
      ),
      footer: (data) => (
        <FooterCell>
          <TableBasicLabel
            text={data.reduce(
              (count, row) =>
                count + Number(getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(5, 'day'))),
              0
            )}
            fontSize={basicFontSize}
            isEdit={isEdit}
          />
        </FooterCell>
      ),
      extraFooter: () =>
        buildWeeklyPerDiemExtraFooter({
          currentDate: dayjs(startDateOfPeriod).add(5, 'day'),
          perdiems,
          allSectionsPerDiems,
          sectionId,
          fullDisabled,
          changePerDiem,
          setOpenPerDiemError,
          t
        })
    },
    {
      id: '10',
      width: styleConfig.date.width,
      name: () => (
        <div className="d-flex flex-column">
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(startDateOfPeriod).add(6, 'day')).format('ddd')}
            fontColor="white"
            fontSize="14px"
            fontWeight="400"
            lineHeight="17px"
          />
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(startDateOfPeriod).add(6, 'day')).format('DD')}
            fontColor="white"
            notNumber
          />
        </div>
      ),
      value: (data) => (
        <EditableLable
          disabled={
            !isEdit ||
            (dayjs(startDateOfPeriod).add(6, 'day').get('month') !== currentMonth &&
              submissionType === submissionEnum.MONTHLY)
          }
          key="key7"
          value={getNumberWithDelimeter(
            getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(6, 'day')),
            isEdit
          )}
          changeHandler={(newValue) => {
            if (isValidByRateType(newValue, rateType)) {
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                newValue,
                dayjs(dayjs(startDateOfPeriod).add(6, 'day')).toString()
              );
            } else {
              setOpenRateError(
                rateType === rateEnum.HOURLY
                  ? t('dashboard.timesheet.validations.max24h')
                  : t('dashboard.timesheet.validations.max1day')
              );
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(6, 'day')),
                dayjs(dayjs(startDateOfPeriod).add(6, 'day')).toString()
              );
            }
          }}
        />
      ),
      footer: (data) => (
        <FooterCell>
          <TableBasicLabel
            text={data.reduce(
              (count, row) =>
                count + Number(getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(6, 'day'))),
              0
            )}
            fontSize={basicFontSize}
            isEdit={isEdit}
          />
        </FooterCell>
      ),
      extraFooter: () =>
        buildWeeklyPerDiemExtraFooter({
          currentDate: dayjs(startDateOfPeriod).add(6, 'day'),
          perdiems,
          allSectionsPerDiems,
          sectionId,
          fullDisabled,
          changePerDiem,
          setOpenPerDiemError,
          t
        })
    },
    {
      id: '11',
      width: styleConfig.date.width,
      name: () => <TableBasicLabel text={t('dashboard.timesheet.table.total')} fontColor="white" />,
      value: (data) => {
        const totalHoursArray = [
          getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod)),
          getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(1, 'day')),
          getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(2, 'day')),
          getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(3, 'day')),
          getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(4, 'day')),
          getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(5, 'day')),
          getHoursByDate(data.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(6, 'day'))
        ];

        return (
          <TableBasicLabel
            text={fixJSfailWithMath(totalHoursArray.reduce((accumulator, hours) => accumulator + Number(hours), 0))}
            fontSize={basicFontSize}
            isEdit={isEdit}
          />
        );
      },
      footer: (data) => (
        <FooterCell>
          <TableBasicLabel
            text={fixJSfailWithMath(
              data.reduce((count, row) => {
                const totalHoursArray = [
                  getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod)),
                  getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(1, 'day')),
                  getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(2, 'day')),
                  getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(3, 'day')),
                  getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(4, 'day')),
                  getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(5, 'day')),
                  getHoursByDate(row.TimesheetHoursByDates, dayjs(startDateOfPeriod).add(6, 'day'))
                ];
                return count + totalHoursArray.reduce((accumulator, hours) => accumulator + Number(hours), 0);
              }, 0)
            )}
            fontSize={basicFontSize}
            isEdit={isEdit}
          />
        </FooterCell>
      ),
      extraFooter: () => {
        return perdiems?.reduce((count, pd) => (pd.IsPerDiem ? count + 1 : count), 0);
      }
    },
    {
      id: '12',
      width: styleConfig.threedot.width,
      name: () => '',
      value: (data) => {
        return (
          isEdit && (
            <ThreeDotMenu menuComponent={<MoreVertOutlinedIcon sx={{ color: colors.ICON_GREY, fontSize: '18px' }} />}>
              <ThreeDotContainer>
                <ThreeDotItem onClick={() => deleteLine({ id: data.Id, sectionId })}>
                  {submissionType === submissionEnum.MONTHLY
                    ? t('dashboard.timesheet.table.deletelinemonthly')
                    : t('dashboard.timesheet.table.deleteline')}
                </ThreeDotItem>
              </ThreeDotContainer>
            </ThreeDotMenu>
          )
        );
      },
      footer: () => <FooterCell radius="0 10px 10px 0" />,
      extraFooter: () => ''
    }
  ];
};

const monthly1columns = (
  sectionId,
  startDateOfPeriod,
  changeHandler,
  t,
  isAllItemsChecked,
  mainCheckboxHandler,
  addHandler,
  allowedWorkTypes,
  isEdit,
  deleteLine,
  rateType,
  currentMonth,
  submissionType,
  setOpenRateError,
  status,
  contractor
) => {
  return [
    {
      id: '2',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.timesheet.table.location')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 15px"
        />
      ),
      width: styleConfigMonthly.location.width,
      value: (data) => (
        <div
          style={{
            width: '100%',
            height: '100%',
            padding: '6px 10px 6px 0px',
            margin: '3.5px 0 3.5px 0'
          }}
        >
          <StyledLocationContainer
            color={colors.NAVY_BLUE}
            height="100%"
            bordercolor={isEdit ? colors.PAGE_BACKGROUND : 'transparent'}
          >
            <SearchLocation
              disabled={!isEdit}
              value={data.Location}
              changeHandler={(value) => changeHandler(TYPES.LOCATION, data.Id, value)}
              withClear={false}
              placeholder={`${t('dashboard.timesheet.table.location')} *`}
            />
          </StyledLocationContainer>
        </div>
      ),
      footer: () =>
        isEdit &&
        ![timesheetsStatuses(t).approved.apiStatus, timesheetsStatuses(t).processed.apiStatus].includes(status) ? (
          <div
            role="presentation"
            onClick={addHandler}
            style={{
              cursor: 'pointer',
              display: 'flex',
              flexWrap: 'nowrap',
              flexDirection: 'row',
              marginRight: 'auto',
              marginTop: '14px',
              marginBottom: '13px',
              gap: '4px',
              color: colors.ICON_GREY
            }}
          >
            <CustomTooltip
              title={t('common.general.addrow')}
              placement="bottom-start"
              theme={{
                backgroundColor: colors.NAVY_BLUE,
                color: colors.WHITE_TXT,
                padding: '6px 8px',
                fontSize: 13,
                borderRadius: '8px'
              }}
            >
              <button style={{ border: 'none', background: 'transparent' }}>
                <AddGradientIcon />
              </button>
            </CustomTooltip>
          </div>
        ) : (
          <div
            role="presentation"
            onClick={addHandler}
            style={{
              height: '24px',
              display: 'flex',
              flexWrap: 'nowrap',
              flexDirection: 'row',
              marginRight: 'auto',
              marginTop: '14px',
              marginBottom: '13px',
              gap: '4px',
              color: colors.ICON_GREY
            }}
          />
        ),
      extraFooter: () => (
        <div className="text-sm text-navy-blue flex h-full">
          <div className="flex items-center gap-2">
            <LocalAtmOutlined />
            <span>Per Diem</span>
          </div>
        </div>
      )
    },
    {
      id: '3',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.timesheet.table.worktype')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 8px"
        />
      ),
      width: styleConfigMonthly.worktype.width,
      value: (data) => (
        <div
          style={{
            width: '100%',
            height: '100%',
            padding: '6px 10px'
          }}
        >
          {isEdit ? (
            <Select
              value={data?.WorkType?.Id}
              onChange={(e) => {
                changeHandler(
                  TYPES.WORK_TYPE,
                  data.Id,
                  allowedWorkTypes.find((allowedType) => allowedType.Id === e.target.value)
                );
                data.WorkType.Id = e.target.value;
              }}
              sx={selectStyles}
              IconComponent={ExpandMoreIcon}
              displayEmpty
              renderValue={data.WorkType !== '' ? undefined : () => `${t('dashboard.timesheet.table.worktype')}*`}
            >
              {allowedWorkTypes.map((type) => (
                <MenuItem key={type.Id} value={type.Id}>
                  {type.Name}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <StyledLabel>{data?.WorkType?.Name}</StyledLabel>
          )}
        </div>
      ),
      footer: () => '',
      extraFooter: () => ''
    },
    {
      id: 'code',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.timesheet.table.code')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 20px"
        />
      ),
      width: styleConfig.comment.width,
      value: (data) => {
        return (
          <TimesheetComment
            disabled={!isEdit}
            title={t('dashboard.timesheet.table.code')}
            comment={data.CostCode}
            author={`${contractor.FirstName} ${contractor.LastName}`}
            changeHandler={(newValue) => changeHandler(TYPES.CODE, data.Id, newValue)}
          />
        );
      },
      footer: () => '',
      extraFooter: () => ''
    },
    {
      id: 'comment',
      name: () => (
        <TableBasicLabel
          text={t('dashboard.timesheet.table.comment')}
          fontColor="white"
          textAlign="left"
          padding="0 0 0 20px"
        />
      ),
      width: styleConfigMonthly.comment.width,
      value: (data) => {
        return (
          <TimesheetComment
            disabled={!isEdit}
            comment={data.Comment}
            author={`${contractor.FirstName} ${contractor.LastName}`}
            changeHandler={(newValue) => changeHandler(TYPES.COMMENT, data.Id, newValue)}
          />
        );
      },
      footer: () => '',
      extraFooter: () => ''
    }
  ];
};

const monthly2columns = (
  sectionId,
  startDateOfPeriod,
  changeHandler,
  t,
  isAllItemsChecked,
  mainCheckboxHandler,
  addHandler,
  allowedWorkTypes,
  isEdit,
  deleteLine,
  rateType,
  currentMonth,
  submissionType,
  setOpenRateError,
  fullData,
  perdiems,
  changePerDiem,
  fullDisabled,
  allSectionsPerDiems,
  setOpenPerDiemError
) => {
  const firstDate = fullData?.[0]?.TimesheetHoursByDates?.[0]?.Day;
  return fullData?.[0]?.TimesheetHoursByDates?.map((_, index) => {
    const currentDate = dayjs(firstDate).add(index, 'day');
    return {
      id: 4 + index,
      width: styleConfigMonthly.date.width,
      name: () => (
        <div className="d-flex flex-column">
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(firstDate).add(index, 'day')).format('ddd')}
            fontColor="white"
            fontSize="14px"
            fontWeight="400"
            lineHeight="17px"
          />
          <TableBasicLabel
            withForceBrake={forcedBrakeWidth}
            text={dayjs(dayjs(firstDate).add(index, 'day')).format('DD')}
            fontColor="white"
            notNumber
          />
        </div>
      ),
      value: (data) => (
        <EditableLable
          key={`key-monthly-${dayjs(firstDate).add(index, 'day')}`}
          disabled={
            !isEdit ||
            (dayjs(firstDate).add(index, 'day').get('month') !== currentMonth &&
              (submissionType === submissionEnum.MONTHLY || submissionType === submissionEnum.BI_MONTHLY))
          }
          value={getNumberWithDelimeter(
            getHoursByDate(data.TimesheetHoursByDates, dayjs(firstDate).add(index, 'day')),
            isEdit
          )}
          sx={{
            backgroundColor:
              dayjs(firstDate).add(index, 'day').day() === 6 || dayjs(firstDate).add(index, 'day').day() === 0
                ? colors.PAGE_BACKGROUND
                : 'transparent',
            color:
              dayjs(firstDate).add(index, 'day').day() === 6 || dayjs(firstDate).add(index, 'day').day() === 0
                ? colors.MEDIUM_GREY
                : colors.NAVY_BLUE
          }}
          changeHandler={(newValue) => {
            if (isValidByRateType(newValue, rateType)) {
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                newValue,
                dayjs(dayjs(firstDate).add(index, 'day')).toString()
              );
            } else {
              setOpenRateError(
                rateType === rateEnum.HOURLY
                  ? t('dashboard.timesheet.validations.max24h')
                  : t('dashboard.timesheet.validations.max1day')
              );
              changeHandler(
                TYPES.HOURS_BY_DATE,
                data.Id,
                getHoursByDate(data.TimesheetHoursByDates, dayjs(firstDate).add(index, 'day')),
                dayjs(dayjs(firstDate).add(index, 'day')).toString()
              );
            }
          }}
        />
      ),
      footer: (data) => (
        <FooterCell
          radius="10px"
          extrastyles={
            dayjs(firstDate).add(index, 'day').day() === 6 || dayjs(firstDate).add(index, 'day').day() === 0
              ? {
                  backgroundColor: colors.PAGE_BACKGROUND,
                  marginLeft: '8px',
                  marginRight: '8px',
                  paddingTop: '16px',
                  paddingBottom: '16px'
                }
              : {}
          }
        >
          <TableBasicLabel
            text={data.reduce(
              (count, row) =>
                count + Number(getHoursByDate(row.TimesheetHoursByDates, dayjs(firstDate).add(index, 'day'))),
              0
            )}
            fontSize={basicFontSize}
            isEdit={isEdit}
          />
        </FooterCell>
      ),
      extraFooter: () => {
        const perDiemItem = perdiems?.find((item) => dayjs(item.Date).isSame(currentDate, 'day'));

        if (!perDiemItem) return null;

        const conflictInOtherSection = allSectionsPerDiems
          ?.filter((sp) => sp.sectionId !== sectionId)
          .some((sp) => sp.perdiems?.some((pd) => dayjs(pd.Date).isSame(currentDate, 'day') && pd.IsPerDiem));

        const isDisabled =
          (dayjs(perDiemItem.Date).month() !== currentMonth && submissionType === submissionEnum.MONTHLY) ||
          fullDisabled ||
          (conflictInOtherSection && !perDiemItem.IsPerDiem);

        return (
          <OutlinedCheckBox
            key={perDiemItem.Date}
            value={perDiemItem.IsPerDiem}
            onChange={() => {
              if (conflictInOtherSection && !perDiemItem.IsPerDiem) {
                setOpenPerDiemError(t('dashboard.timesheet.validations.perdiemDuplicate'));
                return;
              }
              changePerDiem(perDiemItem.Date, !perDiemItem.IsPerDiem, sectionId);
            }}
            disabled={isDisabled}
          />
        );
      }
    };
  });
};

const monthly3columns = (
  sectionId,
  startDateOfPeriod,
  changeHandler,
  t,
  isAllItemsChecked,
  mainCheckboxHandler,
  addHandler,
  allowedWorkTypes,
  isEdit,
  deleteLine,
  rateType,
  currentMonth,
  submissionType,
  perdiems
) => {
  return [
    {
      id: '11',
      width: styleConfigMonthly.total.width,
      name: () => <TableBasicLabel text={t('dashboard.timesheet.table.total')} fontColor="white" />,
      value: (data) => {
        return (
          <div style={{ margin: '21px 0' }}>
            <TableBasicLabel
              text={fixJSfailWithMath(
                data?.TimesheetHoursByDates?.reduce((accumulator, hours) => accumulator + Number(hours.TotalHrs), 0)
              )}
              fontSize={basicFontSize}
              isEdit={isEdit}
            />
          </div>
        );
      },
      footer: (data) => (
        <div style={{ margin: '8px 0' }}>
          <FooterCell>
            <TableBasicLabel
              text={fixJSfailWithMath(
                data.reduce(
                  (acc, row) =>
                    acc +
                    (row?.TimesheetHoursByDates?.reduce(
                      (accumulator, hours) => accumulator + Number(hours.TotalHrs),
                      0
                    ) ?? 0),
                  0
                )
              )}
              fontSize={basicFontSize}
              isEdit={isEdit}
            />
          </FooterCell>
        </div>
      ),
      extraFooter: () => {
        return perdiems?.reduce((count, pd) => (pd.IsPerDiem ? count + 1 : count), 0);
      }
    },
    {
      id: '12',
      width: styleConfigMonthly.threedot.width,
      name: () => '',
      value: (data) => {
        return (
          isEdit && (
            <ThreeDotMenu menuComponent={<MoreVertOutlinedIcon sx={{ color: colors.ICON_GREY, fontSize: '18px' }} />}>
              <ThreeDotContainer>
                <ThreeDotItem onClick={() => deleteLine({ id: data.Id, sectionId })}>
                  {submissionType === submissionEnum.MONTHLY
                    ? t('dashboard.timesheet.table.deletelinemonthly')
                    : t('dashboard.timesheet.table.deleteline')}
                </ThreeDotItem>
              </ThreeDotContainer>
            </ThreeDotMenu>
          )
        );
      },
      footer: () => <FooterCell radius="0 10px 10px 0" />,
      extraFooter: () => ''
    }
  ];
};

export { columns, monthly1columns, monthly2columns, monthly3columns };
